import { IonContent, IonModal, IonSpinner, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useLocaleCapture } from '../../hooks/useLocaleCapture';
import { useAddressCapture } from '../../hooks/useAddressCapture';
import { useAutoCaptureOnPermissionGrant } from '../../hooks/useAutoCaptureOnPermissionGrant';
import { useLocationStore } from '../../store/useLocationStore';
import { LocaleSearchInput } from './LocaleSearchInput';

/**
 * Routes whose reads are scoped server-side by `profile.currentLocaleId` but
 * carry no locale in their URL/query key (e.g. dish detail at `/listings/:id`).
 * Changing locale silently re-stamps `currentLocaleId` on the BE, so the next
 * refetch of one of these pages resolves against the new locale and 404s when
 * the resource doesn't live there. Until each page can handle a cross-locale
 * read on its own, we bounce the user back to the home feed on locale change
 * so they never sit on a page that's about to 404 under them.
 *
 * Patterns are matched with react-router's `matchPath` (same syntax as the
 * <Route path> definitions in App.tsx). Add surfaces here as they're found.
 */
const LOCALE_CHANGE_REDIRECT_PATHS = ['/listing/:id'];

interface LocaleSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Locale-change modal opened from the TopBar pill.
 *
 * Reads `locale.name` for the current label (the TopBar pill is canonically
 * the operating-locale name — this surface stays consistent with it).
 *
 * Two paths, one source of truth (`locationSource`), and NEITHER touches the
 * saved profile address — this is a browse switch, not an address editor:
 *  - "Use my current location" goes through `useAddressCapture` in store-only
 *    mode (`mirrorMode: 'browse'`) — it writes precise device coords +
 *    `locationSource = 'device'` to the location store, which lights the
 *    crosshair AND unlocks dish-card distances (the same flag the distance gate
 *    and device-location guard read), but writes nothing to the profile.
 *  - A typed ZIP or a city picked from autocomplete goes through
 *    `useLocaleCapture` → `locationSource = 'postal'`: no crosshair, no
 *    distance, feed anchored on the centroid.
 *
 * For dedicated address editing (order gate, profile address) use
 * `AddressCaptureModal` instead.
 */
export const LocaleSelectorModal: React.FC<LocaleSelectorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('location');
  const history = useHistory();
  const { pathname } = useLocation();
  const currentLocale = useLocationStore((s) => s.locale);
  const locationSource = useLocationStore((s) => s.locationSource);
  const localeZip = useLocationStore((s) => s.localeZip);

  // Shared by both capture paths: if the user changed locale while on a page
  // whose reads are scoped by the (now-changed) server-side locale, bounce to
  // the home feed before its next refetch 404s under them. `replace` so the
  // back button doesn't land them back on the about-to-404 page. See
  // LOCALE_CHANGE_REDIRECT_PATHS.
  const handleCaptured = () => {
    const onLocaleScopedPage = LOCALE_CHANGE_REDIRECT_PATHS.some((path) =>
      matchPath(pathname, { path, exact: true }),
    );
    if (onLocaleScopedPage) {
      history.replace('/');
    }
    onClose();
  };

  // Device path: writes precise coords + locationSource='device' to the STORE
  // only (mirrorMode 'browse') — this is a browse switch, so it must NOT touch the
  // saved profile address. That unlocks the crosshair + distance; InitLocale
  // re-resolves the locale from the new coords.
  const { geoLoading, geoError, handleUseDeviceLocation } = useAddressCapture({
    mirrorMode: 'browse',
    onCaptured: handleCaptured,
  });

  // While this switcher is open, turning browser location on (native prompt /
  // site settings, not just the button) captures through the same flow and
  // closes/redirects via handleCaptured. Scoped to `isOpen` so a grant while
  // browsing elsewhere never silently switches the user's browse location.
  useAutoCaptureOnPermissionGrant(isOpen, handleUseDeviceLocation);

  // Search path (city or ZIP): locationSource='postal' (no crosshair, no distance).
  const { query, setQuery, searchError, handleSearchSubmit, searchSubmitting } = useLocaleCapture({
    onCaptured: handleCaptured,
  });

  const currentLabel = currentLocale?.name || t('selector.unknownLocation');

  // Provenance line: tells the user whether the operating locale came from
  // their device location or a location they entered (naming the ZIP when they
  // typed one — a city search has no ZIP to echo, and the locale name above
  // already says where they are). Reads the single `locationSource` flag.
  // Auto-resolved / login locales leave it null and we show the name alone.
  const sourceLine =
    locationSource === 'device'
      ? t('locale.sourceDevice')
      : locationSource === 'postal'
        ? localeZip
          ? t('locale.sourceZip', { zip: localeZip })
          : t('locale.sourceZipUnknown')
        : null;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{currentLocale ? t('locale.title') : t('locale.setTitle')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>{t('common:actions.close')}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="mx-auto flex w-full max-w-md flex-col gap-6 px-6 py-8">
          {currentLocale && (
            <div className="rounded-xl border border-border bg-surface-secondary p-4 text-center">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('locale.currentLabel')}</p>
              <p className="mt-1 text-base font-semibold">{currentLabel}</p>
              {sourceLine && <p className="mt-1 text-sm text-muted-foreground">{sourceLine}</p>}
            </div>
          )}

          <div className="space-y-2">
            <button
              type="button"
              onClick={handleUseDeviceLocation}
              disabled={geoLoading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
            >
              {geoLoading ? (
                <>
                  <IonSpinner name="crescent" className="h-5 w-5" />
                  {t('gettingLocation')}
                </>
              ) : (
                t('useDeviceButton')
              )}
            </button>
            {geoError && <p className="text-center text-sm text-brand-danger">{geoError}</p>}
          </div>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{t('divider.or')}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <LocaleSearchInput
            inputId="change-locale-search"
            value={query}
            onChange={setQuery}
            onSubmit={handleSearchSubmit}
            submitting={searchSubmitting}
            error={searchError}
            label={t('locale.searchLabel')}
            placeholder={t('search.placeholder')}
            ctaLabel={t('selector.updateCta')}
          />
        </div>
      </IonContent>
    </IonModal>
  );
};
